import consola from "consola"

import {
  pickHighestSupportedEffort,
  type ReasoningEffort,
} from "~/lib/anthropic-sanitizer"
import { state } from "~/lib/state"
import { findModel } from "~/lib/utils"
import {
  type ChatCompletionResponse,
  type ChatCompletionsPayload,
  type ContentPart,
  type Message,
  type TextPart,
  type Tool,
  type ToolCall,
} from "~/services/copilot/create-chat-completions"

import {
  type AnthropicAssistantContentBlock,
  type AnthropicAssistantMessage,
  type AnthropicMessage,
  type AnthropicMessagesPayload,
  type AnthropicResponse,
  type AnthropicTextBlock,
  type AnthropicThinkingBlock,
  type AnthropicTool,
  type AnthropicToolResultBlock,
  type AnthropicToolUseBlock,
  type AnthropicUserContentBlock,
  type AnthropicUserMessage,
} from "./anthropic-types"
import { mapOpenAIStopReasonToAnthropic } from "./utils"

// Payload translation

export function translateToOpenAI(
  payload: AnthropicMessagesPayload,
): ChatCompletionsPayload {
  const toolChoice = translateAnthropicToolChoiceToOpenAI(payload.tool_choice)
  // Thinking cannot be enabled when tool_choice forces tool use
  const isForced =
    Boolean(toolChoice) && toolChoice !== "auto" && toolChoice !== "none"
  const reasoningEffort = pickTranslatedReasoningEffort(payload)

  return {
    model: translateModelName(payload.model),
    messages: translateAnthropicMessagesToOpenAI(
      payload.messages,
      payload.system,
    ),
    max_tokens: payload.max_tokens,
    stop: payload.stop_sequences,
    stream: payload.stream,
    temperature: payload.temperature,
    top_p: payload.top_p,
    user: payload.metadata?.user_id,
    tools: translateAnthropicToolsToOpenAI(payload.tools),
    tool_choice: toolChoice,
    // Convert Anthropic thinking to Copilot thinking parameters.
    // Always request maximum thinking: reasoning_effort="high".
    // If a model rejects "high" (e.g. claude-opus-4.7 only accepts "medium"),
    // the downstream error handler in createChatCompletions will auto-downgrade.
    ...(!isForced
      && payload.thinking && {
        reasoning_effort: reasoningEffort,
      }),
    ...(!isForced
      && payload.thinking?.budget_tokens && {
        thinking_budget: payload.thinking.budget_tokens,
      }),
  }
}

function pickTranslatedReasoningEffort(
  payload: AnthropicMessagesPayload,
): ReasoningEffort {
  const requested = payload.output_config?.effort ?? payload.effort
  if (requested && requested !== "max") return requested

  const model = findModel(translateModelName(payload.model))
  const advertised = pickHighestSupportedEffort(
    model?.capabilities.supports.reasoning_effort,
  )
  return advertised ?? "high"
}

export function translateModelName(model: string): string {
  // Claude Code 发送的模型名称可能与 GitHub Copilot 支持的不一致
  // 需要智能匹配到 Copilot 实际支持的模型

  const supportedModels = state.models?.data.map((m) => m.id) ?? []

  // 1. 直接匹配
  if (supportedModels.includes(model)) {
    return model
  }

  // 2. 移除日期后缀后匹配 (claude-opus-4-5-20251101 -> claude-opus-4-5)
  const modelBase = model.replace(/-\d{8}$/, "")
  if (supportedModels.includes(modelBase)) {
    consola.debug(
      `Model name: "${model}" → "${modelBase}" (stripped date suffix)`,
    )
    return modelBase
  }

  // 3. 尝试 4-5 -> 4.5 格式转换 (claude-opus-4-5 -> claude-opus-4.5)
  const modelWithDot = modelBase.replace(/-(\d+)-(\d+)$/, "-$1.$2")
  if (supportedModels.includes(modelWithDot)) {
    consola.debug(`Model name: "${model}" → "${modelWithDot}" (dash→dot)`)
    return modelWithDot
  }

  // 4. 尝试 4.5 -> 4-5 格式转换 (claude-opus-4.5 -> claude-opus-4-5)
  const modelWithDash = model.replace(/(\d+)\.(\d+)/, "$1-$2")
  if (supportedModels.includes(modelWithDash)) {
    consola.debug(`Model name: "${model}" → "${modelWithDash}" (dot→dash)`)
    return modelWithDash
  }

  // 5. Anthropic 旧格式到 Copilot 新格式映射
  // claude-3-5-sonnet-xxx -> claude-sonnet-4.5
  // claude-3-opus-xxx -> claude-opus-4.5
  // claude-3-5-haiku-xxx -> claude-haiku-4.5
  const oldFormatMapping: Record<string, string> = {
    "claude-3-5-sonnet": "claude-sonnet-4.5",
    "claude-3-sonnet": "claude-sonnet-4",
    "claude-3-5-opus": "claude-opus-4.5",
    "claude-3-opus": "claude-opus-4.5",
    "claude-3-5-haiku": "claude-haiku-4.5",
    "claude-3-haiku": "claude-haiku-4.5",
  }

  for (const [oldFormat, newFormat] of Object.entries(oldFormatMapping)) {
    if (
      modelBase.startsWith(oldFormat)
      && supportedModels.includes(newFormat)
    ) {
      consola.debug(`Model name: "${model}" → "${newFormat}" (legacy mapping)`)
      return newFormat
    }
  }

  // 6. 如果都找不到，返回原始模型名（会被 Copilot 拒绝，让上层处理）
  consola.warn(
    `Model name: "${model}" not found in supported models list, passing as-is`,
  )
  return model
}

function translateAnthropicMessagesToOpenAI(
  anthropicMessages: Array<AnthropicMessage>,
  system: string | Array<AnthropicTextBlock> | undefined,
): Array<Message> {
  const systemMessages = handleSystemPrompt(system)

  const otherMessages = anthropicMessages.flatMap((message) =>
    message.role === "user" ?
      handleUserMessage(message)
    : handleAssistantMessage(message),
  )

  return [...systemMessages, ...otherMessages]
}

function handleSystemPrompt(
  system: string | Array<AnthropicTextBlock> | undefined,
): Array<Message> {
  if (!system) {
    return []
  }

  if (typeof system === "string") {
    return [{ role: "system", content: system }]
  } else {
    const systemText = system.map((block) => block.text).join("\n\n")
    return [{ role: "system", content: systemText }]
  }
}

function handleUserMessage(message: AnthropicUserMessage): Array<Message> {
  const newMessages: Array<Message> = []

  if (Array.isArray(message.content)) {
    const toolResultBlocks = message.content.filter(
      (block): block is AnthropicToolResultBlock =>
        block.type === "tool_result",
    )
    const otherBlocks = message.content.filter(
      (block) => block.type !== "tool_result",
    )

    // Tool results must come first to maintain protocol: tool_use -> tool_result -> user
    for (const block of toolResultBlocks) {
      newMessages.push({
        role: "tool",
        tool_call_id: block.tool_use_id,
        content: mapContent(block.content),
      })
    }

    if (otherBlocks.length > 0) {
      newMessages.push({
        role: "user",
        content: mapContent(otherBlocks),
      })
    }
  } else {
    newMessages.push({
      role: "user",
      content: mapContent(message.content),
    })
  }

  return newMessages
}

function handleAssistantMessage(
  message: AnthropicAssistantMessage,
): Array<Message> {
  if (!Array.isArray(message.content)) {
    return [
      {
        role: "assistant",
        content: mapContent(message.content),
      },
    ]
  }

  const toolUseBlocks = message.content.filter(
    (block): block is AnthropicToolUseBlock => block.type === "tool_use",
  )

  const textBlocks = message.content.filter(
    (block): block is AnthropicTextBlock => block.type === "text",
  )

  const thinkingBlocks = message.content.filter(
    (block): block is AnthropicThinkingBlock => block.type === "thinking",
  )

  // Combine text and thinking blocks, as OpenAI doesn't have separate thinking blocks
  const allTextContent = [
    ...textBlocks.map((b) => b.text),
    ...thinkingBlocks.map((b) => b.thinking),
  ].join("\n\n")

  return toolUseBlocks.length > 0 ?
      [
        {
          role: "assistant",
          content: allTextContent || null,
          tool_calls: toolUseBlocks.map((toolUse) => ({
            id: toolUse.id,
            type: "function",
            function: {
              name: toolUse.name,
              arguments: JSON.stringify(toolUse.input),
            },
          })),
        },
      ]
    : [
        {
          role: "assistant",
          content: mapContent(message.content),
        },
      ]
}

function mapContent(
  content:
    | string
    | Array<AnthropicUserContentBlock | AnthropicAssistantContentBlock>,
): string | Array<ContentPart> | null {
  if (typeof content === "string") {
    return content
  }
  if (!Array.isArray(content)) {
    return null
  }

  const hasImage = content.some((block) => block.type === "image")
  if (!hasImage) {
    return content
      .filter(
        (block): block is AnthropicTextBlock | AnthropicThinkingBlock =>
          block.type === "text" || block.type === "thinking",
      )
      .map((block) => (block.type === "text" ? block.text : block.thinking))
      .join("\n\n")
  }

  const contentParts: Array<ContentPart> = []
  for (const block of content) {
    switch (block.type) {
      case "text": {
        contentParts.push({ type: "text", text: block.text })

        break
      }
      case "thinking": {
        contentParts.push({ type: "text", text: block.thinking })

        break
      }
      case "image": {
        contentParts.push({
          type: "image_url",
          image_url: {
            url: `data:${block.source.media_type};base64,${block.source.data}`,
          },
        })

        break
      }
      // No default
    }
  }
  return contentParts
}

function translateAnthropicToolsToOpenAI(
  anthropicTools: Array<AnthropicTool> | undefined,
): Array<Tool> | undefined {
  if (!anthropicTools) {
    return undefined
  }
  // Server-side tools (web_search_*, web_fetch_*, bash_*, computer_*,
  // text_editor_*, code_execution_*) have a `type` field and no
  // `input_schema` — OpenAI chat-completions cannot describe them, so
  // skip them silently. The native /v1/messages path is the supported
  // route for server-side tools; web_search/web_fetch get a dedicated
  // fallback (see proxy-web-fallback.ts).
  const translated: Array<Tool> = []
  for (const tool of anthropicTools) {
    if (
      "type" in tool
      && typeof (tool as { type?: unknown }).type === "string"
    ) {
      continue
    }
    const custom = tool as {
      name: string
      description?: string
      input_schema: Record<string, unknown>
    }
    translated.push({
      type: "function",
      function: {
        name: custom.name,
        description: custom.description,
        parameters: custom.input_schema,
      },
    })
  }
  return translated.length > 0 ? translated : undefined
}

function translateAnthropicToolChoiceToOpenAI(
  anthropicToolChoice: AnthropicMessagesPayload["tool_choice"],
): ChatCompletionsPayload["tool_choice"] {
  if (!anthropicToolChoice) {
    return undefined
  }

  switch (anthropicToolChoice.type) {
    case "auto": {
      return "auto"
    }
    case "any": {
      return "required"
    }
    case "tool": {
      if (anthropicToolChoice.name) {
        return {
          type: "function",
          function: { name: anthropicToolChoice.name },
        }
      }
      return undefined
    }
    case "none": {
      return "none"
    }
    default: {
      return undefined
    }
  }
}

// Response translation

// eslint-disable-next-line complexity
export function translateToAnthropic(
  response: ChatCompletionResponse,
): AnthropicResponse {
  // Merge content from all choices
  const allThinkingBlocks: Array<AnthropicThinkingBlock> = []
  const allTextBlocks: Array<AnthropicTextBlock> = []
  const allToolUseBlocks: Array<AnthropicToolUseBlock> = []
  let stopReason: "stop" | "length" | "tool_calls" | "content_filter" | null =
    null // default
  stopReason = response.choices[0]?.finish_reason ?? stopReason

  // Process all choices to extract thinking, text and tool use blocks
  for (const choice of response.choices) {
    const thinkingBlocks = getAnthropicThinkingBlocks(
      choice.message.reasoning_content ?? choice.message.reasoning_text,
    )
    const textBlocks = getAnthropicTextBlocks(choice.message.content)
    const toolUseBlocks = getAnthropicToolUseBlocks(choice.message.tool_calls)

    allThinkingBlocks.push(...thinkingBlocks)
    allTextBlocks.push(...textBlocks)
    allToolUseBlocks.push(...toolUseBlocks)

    // Use the finish_reason from the first choice, or prioritize tool_calls
    if (choice.finish_reason === "tool_calls" || stopReason === "stop") {
      stopReason = choice.finish_reason
    }
  }

  return {
    id: response.id,
    type: "message",
    role: "assistant",
    model: response.model,
    // Anthropic convention: thinking blocks come first, then text, then tool_use
    content: [...allThinkingBlocks, ...allTextBlocks, ...allToolUseBlocks],
    stop_reason: mapOpenAIStopReasonToAnthropic(stopReason),
    stop_sequence: null,
    usage: {
      input_tokens:
        (response.usage?.prompt_tokens ?? 0)
        - (response.usage?.prompt_tokens_details?.cached_tokens ?? 0),
      output_tokens: response.usage?.completion_tokens ?? 0,
      ...(response.usage?.prompt_tokens_details?.cached_tokens
        !== undefined && {
        cache_read_input_tokens:
          response.usage.prompt_tokens_details.cached_tokens,
      }),
    },
  }
}

function getAnthropicThinkingBlocks(
  reasoningContent: string | null | undefined,
): Array<AnthropicThinkingBlock> {
  if (!reasoningContent) {
    return []
  }
  return [{ type: "thinking", thinking: reasoningContent }]
}

function getAnthropicTextBlocks(
  messageContent: Message["content"],
): Array<AnthropicTextBlock> {
  if (typeof messageContent === "string") {
    return [{ type: "text", text: messageContent }]
  }

  if (Array.isArray(messageContent)) {
    return messageContent
      .filter((part): part is TextPart => part.type === "text")
      .map((part) => ({ type: "text", text: part.text }))
  }

  return []
}

function getAnthropicToolUseBlocks(
  toolCalls: Array<ToolCall> | undefined,
): Array<AnthropicToolUseBlock> {
  if (!toolCalls) {
    return []
  }
  return toolCalls.map((toolCall) => ({
    type: "tool_use",
    id: toolCall.id,
    name: toolCall.function.name,
    input: (() => {
      try {
        return JSON.parse(toolCall.function.arguments) as Record<
          string,
          unknown
        >
      } catch {
        return {}
      }
    })(),
  }))
}
